import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import FolderExplorer from "./FolderExplorer.js";

export interface FolderExplorerModalProps {
	/** Called with the selected folder ID when the user confirms their choice. */
	onSelect: (folderId: string) => void;
	/** Called when the modal is dismissed without a selection. */
	onClose: () => void;
}

/**
 * Windows Explorer-styled modal for browsing and selecting a WorkDrive folder.
 * Rendered via React Portal so it sits outside the normal component tree.
 *
 * Wraps the reusable FolderExplorer component in a modal overlay with
 * focus trapping and keyboard handling.
 */
export default function FolderExplorerModal({
	onSelect,
	onClose,
}: FolderExplorerModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);

	/* ------------------------------------------------------------------ */
	/*  Keyboard handling (Escape + focus trap)                             */
	/* ------------------------------------------------------------------ */
	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent): void {
			if (event.key === "Escape") {
				onClose();
				return;
			}

			if (event.key === "Tab") {
				const modal = modalRef.current;
				if (!modal) return;

				const focusable = Array.from(
					modal.querySelectorAll<HTMLElement>(
						'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					),
				).filter((el) => !el.closest("[aria-hidden]"));

				if (focusable.length === 0) return;

				const first = focusable[0];
				const last = focusable[focusable.length - 1];

				if (event.shiftKey) {
					if (document.activeElement === first) {
						event.preventDefault();
						last.focus();
					}
				} else {
					if (document.activeElement === last) {
						event.preventDefault();
						first.focus();
					}
				}
			}
		}

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onClose]);

	/* Focus the modal container on mount */
	useEffect(() => {
		modalRef.current?.focus();
	}, []);

	/* ------------------------------------------------------------------ */
	/*  Selection handler — adapts FolderExplorer's (id, name) to modal's (id) */
	/* ------------------------------------------------------------------ */
	function handleSelect(folderId: string, _folderName: string): void {
		onSelect(folderId);
	}

	/* ------------------------------------------------------------------ */
	/*  Backdrop click                                                      */
	/* ------------------------------------------------------------------ */
	function handleBackdropClick(event: React.MouseEvent<HTMLDivElement>): void {
		if (event.target === event.currentTarget) {
			onClose();
		}
	}

	const modal = (
		<div
			className="folder-explorer-overlay"
			role="presentation"
			onClick={handleBackdropClick}
		>
			<div
				ref={modalRef}
				className="folder-explorer-window"
				role="dialog"
				aria-modal="true"
				aria-label="Select WorkDrive folder"
				tabIndex={-1}
			>
				{/* Title bar */}
				<div className="folder-explorer-titlebar">
					<span className="folder-explorer-titlebar-icon" aria-hidden="true">
						📁
					</span>
					<span className="folder-explorer-titlebar-title">
						Select WorkDrive Folder
					</span>
					<button
						type="button"
						className="folder-explorer-close-btn"
						onClick={onClose}
						aria-label="Close folder explorer"
					>
						&times;
					</button>
				</div>

				{/* Reusable folder browser */}
				<FolderExplorer
					onSelect={handleSelect}
					onClose={onClose}
				/>
			</div>
		</div>
	);

	return createPortal(modal, document.body);
}
